import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { FiMenu, FiX, FiSun, FiMoon } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { signIn, signup } from "../../redux/features/counterSlice";
import { useThemeToogle } from "../../hooks/themeContext";

const Header = () => {
  const { isDark, toggleDarkMode } = useThemeToogle();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 2);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);

  }, []);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const scrollToSection = (e, sectionId) => {
    if (location.pathname === "/") {
      e.preventDefault();
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
      setMobileMenuOpen(false);
    }
  };

  const handleSignIn = () => dispatch(signIn());
  const handleSignUp = () => dispatch(signup());

  const isHomePage = location.pathname === "/";
  const bgColor = isHomePage
    ? scrolled
      ? "bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm shadow-sm"
      : "bg-transparent"
    : "bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm shadow-sm";

  return (
    <header className={`fixed w-[100vw] z-50 ${bgColor} transition-all duration-300`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2">
            {/* <motion.div
            //   className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg"
            //   whileHover={{ scale: 1.05 }}
            // /> */}
            <span className="text-xl font-bold text-gray-1000 dark:text-white">
              STACKLY
            </span>
          </Link>

          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="#features"
              onClick={(e) => scrollToSection(e, "features")}
              className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              Features
            </Link>
            <Link
              to="#pricing"
              onClick={(e) => scrollToSection(e, "pricing")}
              className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              Pricing
            </Link>
            <Link
              to="#support"
              onClick={(e) => scrollToSection(e, "support")}
              className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              Support
            </Link>
            <Link
              to="/blog"
              className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              Blog
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <FiSun size={20} /> : <FiMoon size={20} />}
            </button>
            <Link to="/signin">
              <motion.button
                onClick={handleSignIn}
                className="px-4 py-2 font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Sign In
              </motion.button>
            </Link>
            <Link to="/signup">
              <motion.button
                onClick={handleSignUp}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started
              </motion.button>
            </Link>
          </div>

          <div className="flex md:hidden items-center gap-4">
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <FiSun size={20} /> : <FiMoon size={20} />}
            </button>
            <button
              onClick={toggleMobileMenu}
              className="  p-2 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <motion.div
        initial={false}
        animate={mobileMenuOpen ? "open" : "closed"}
        variants={{
          open: { opacity: 1, height: "auto" },
          closed: { opacity: 0, height: 0 }
        }}
        className="md:hidden overflow-hidden bg-white dark:bg-gray-900 shadow-lg"
      >
        <div className="px-4 text-center pt-2 pb-6 space-y-4">
          <Link
            to="#features"
            onClick={(e) => scrollToSection(e, "features")}
            className="block py-2 font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            Features
          </Link>
          <Link
            to="#pricing"
            onClick={(e) => scrollToSection(e, "pricing")}
            className="block py-2 font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            Pricing
          </Link>
          <Link
            to="#support"
            onClick={(e) => scrollToSection(e, "support")}
            className="block py-2 font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            Support
          </Link>
          <Link
            to="/blog"
            className="block py-2 font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            Blog
          </Link>
          <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
            <Link to="/signin" onClick={toggleMobileMenu}>
              <motion.button
                onClick={handleSignIn}
                className="block w-full py-2 font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"

                whileTap={{ scale: 0.95 }}
              >
                Sign In
              </motion.button>
            </Link>
            <Link to="/signup" onClick={toggleMobileMenu}>
              <motion.button
                onClick={handleSignUp}
                className="block w-full mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md text-center transition-colors"

                whileTap={{ scale: 0.95 }}
              >
                Get Started
              </motion.button>
            </Link>
          </div>
        </div>
      </motion.div>
    </header>
  );
};

export default Header;